import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api, { reportsAPI, dataAPI } from '../../services/api';
import MetricCard from '../Common/MetricCard';
import ComboChart from '../Charts/ComboChart';
import DateSelector from '../Common/DateSelector';
import RadioTable from '../Tables/RadioTable';
import TelevisionTable from '../Tables/TelevisionTable';
import SportSignageTable from '../Tables/SportSignageTable';
import DistributorSelector from '../Common/DistributorSelector';
import RegionSelector from '../Common/RegionSelector';
import DistrictSelector from '../Common/DistrictSelector';
import MapView from '../Maps/MapView';
import DashboardHeader from '../Common/DashboardHeader';
import TabNavigation, { Tab } from '../Common/TabNavigation';
import UserManagementView from '../Admin/UserManagementView';
import CoverageAnalysisView from '../Admin/CoverageAnalysisView';
import AlertManagementView from '../Admin/AlertManagementView';
import DealerDashboard from './DealerDashboard';
import LazyLoad from '../Common/LazyLoad';
import { AllReportData, CHANNEL_COLORS, Distributor } from '../../types';
import { hasMetricData, hasChartData, hasTableData } from '../../utils/dataValidation';
import KnowledgePanel from '../Common/KnowledgePanel';
import { getDefinition } from '../../data/advertisingDefinitions';
import { format, subDays } from 'date-fns';
import './dashboard-common.css';
import './CorporateDashboard.css';

interface Region {
  id: string;
  name: string;
  manager: string;
  username: string;
  district_count: number;
  dealer_count: number;
  total_impressions?: number;
  channels?: Record<string, number>;
}

interface District {
  id: string;
  name: string;
  manager: string;
  username: string;
  regional_manager: string;
  dealer_count: number;
  total_impressions?: number;
  channels?: Record<string, number>;
}

const CorporateDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState<AllReportData | null>(null);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null);
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [districts, setDistricts] = useState<District[]>([]);
  const [selectedDistrict, setSelectedDistrict] = useState<District | null>(null);
  const [dealerLocations, setDealerLocations] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'table' | 'map' | 'chart' | 'combined'>('combined');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [spotsData, setSpotsData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [mapLoading, setMapLoading] = useState(false);
  const [knowledgePanelOpen, setKnowledgePanelOpen] = useState(false);
  const [currentTopic, setCurrentTopic] = useState<string>('');
  const [mlbSocialData, setMlbSocialData] = useState<{ views: number; engagement: number } | null>(null);

  const handleHelpClick = (topic: string) => {
    if (knowledgePanelOpen && currentTopic === topic) {
      setKnowledgePanelOpen(false);
    } else {
      setCurrentTopic(topic);
      setKnowledgePanelOpen(true);
    }
  };

  // DEBUG: Log component state on every render
  console.log('[CORPORATE DASHBOARD DEBUG]', {
    user_role: user?.role,
    user_is_corporate: user?.is_corporate,
    user_dealer_id: user?.dealer_id,
    selectedDistributor: selectedDistributor,
    selectedDistributor_name: selectedDistributor?.name,
    selectedRegion: selectedRegion,
    selectedDistrict: selectedDistrict,
    activeTab: activeTab
  });

  // DEBUG: Wrap setSelectedDistributor to log when it's called
  const originalSetSelectedDistributor = setSelectedDistributor;
  const debugSetSelectedDistributor = (value: Distributor | null) => {
    console.log('[CORPORATE DASHBOARD] setSelectedDistributor called!', {
      new_value: value,
      stack: new Error().stack
    });
    originalSetSelectedDistributor(value);
  };

  const getDefaultStartDate = () => {
    // Campaign started September 1, 2025 - always use this as default start date
    return new Date(2025, 8, 1); // September 1, 2025 (month 8 = September)
  };

  const [dateRange, setDateRange] = useState({
    startDate: getDefaultStartDate(),
    endDate: new Date()
  });
  const isFetchingRef = useRef(false);
  const mapLoadedRef = useRef(false); // tracks if corporate map data is loaded for the current data refresh

  // Load regions on mount and when date range changes
  useEffect(() => {
    fetchRegions();
    fetchDistributors();
  }, [dateRange.startDate, dateRange.endDate]);

  // Load districts when region changes or date range changes
  useEffect(() => {
    if (selectedRegion) {
      fetchDistricts(selectedRegion.id);
    } else {
      setDistricts([]);
      setSelectedDistrict(null);
    }
  }, [selectedRegion, dateRange.startDate, dateRange.endDate]);

  // Load dealers when district changes
  useEffect(() => {
    if (selectedDistrict) {
      fetchDistrictDealers(selectedDistrict.id, selectedDistrict.username);
    }
  }, [selectedDistrict]);

  // Fetch report data when filters change
  useEffect(() => {
    // Clear old data immediately to show skeletons during load
    setReportData(null);
    setIsLoading(true);
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate, selectedDistributor?.distributor_id, selectedDistrict?.username, selectedRegion?.username]);

  const fetchRegions = async () => {
    try {
      const startDate = format(dateRange.startDate, 'yyyy-MM-dd');
      const endDate = format(dateRange.endDate, 'yyyy-MM-dd');

      // Single batched call — backend runs all region queries in parallel
      const response = await api.get(`/api/v2/reports/dashboard/all-regions`, {
        params: { start_date: startDate, end_date: endDate }
      });

      setRegions(response.data?.regions || []);
    } catch (err: any) {
      console.error('Failed to load regions:', err);
    }
  };

  const fetchDistricts = async (regionId: string) => {
    try {
      const startDate = format(dateRange.startDate, 'yyyy-MM-dd');
      const endDate = format(dateRange.endDate, 'yyyy-MM-dd');

      // Single batched call — backend runs all district queries in parallel
      const response = await api.get(`/api/v2/reports/dashboard/all-districts-for-region`, {
        params: { region_id: regionId, start_date: startDate, end_date: endDate }
      });

      const districtsList: District[] = response.data?.districts || [];
      setDistricts(districtsList);
    } catch (err: any) {
      console.error('Failed to load districts:', err);
    }
  };

  const fetchDistrictDealers = async (districtId: string, districtUsername: string) => {
    try {
      // Fetch dealer metrics from district dashboard API (includes impressions per dealer)
      const startDate = format(dateRange.startDate, 'yyyy-MM-dd');
      const endDate = format(dateRange.endDate, 'yyyy-MM-dd');

      const districtData = await api.get(`/api/v2/reports/dashboard`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          district_manager: districtUsername
        }
      });

      // The district API returns { total: {...}, dealers: [...] }
      const dealersWithMetrics = districtData.data?.dealers || [];

      // Convert to distributor format with metrics
      const distributorsList = dealersWithMetrics.map((dealer: any) => ({
        dealer_id: dealer.dealer_id,  // Keep original dealer_id string for API calls
        distributor_id: parseInt(dealer.dealer_id),
        name: dealer.dealer_name,
        address: '',
        city: dealer.dma_name || '',  // Use DMA name as location for now
        state: '',
        zip: '',
        dma_code: dealer.dma_code || '',
        email: '',
        // Add metrics for table display
        total_impressions: dealer.impressions || 0,
        total_clicks: dealer.clicks || 0
      }));

      setDistributors(distributorsList);
      setSelectedDistributor(null);
    } catch (err: any) {
      console.error('Failed to load district dealers:', err);
    }
  };

  const fetchDistributors = async () => {
    try {
      const response = await dataAPI.getDistributors();
      // Extract the data array from the response
      const distributorsList = response.data || [];
      setDistributors(distributorsList);
      // Don't auto-select a distributor for corporate view
      setSelectedDistributor(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load distributors');
    }
  };

  const fetchReportData = async () => {
    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    mapLoadedRef.current = false; // reset so map refetches when user opens map tab
    setIsLoading(true);
    setError(null);

    try {
      const startDate = format(dateRange.startDate, 'yyyy-MM-dd');
      const endDate = format(dateRange.endDate, 'yyyy-MM-dd');
      const dealerId = selectedDistributor?.distributor_id ? String(selectedDistributor.distributor_id) : undefined;
      const districtManager = selectedDistrict?.username || undefined;
      const regionalManager = selectedRegion?.username || undefined; // Use username for API queries, not display name

      console.log('[OPTIMIZATION #2] Using consolidated endpoint - single API call instead of 10!');
      console.log(`[CORPORATE] Filters: dealer=${dealerId}, district=${districtManager}, region=${regionalManager}`);
      const consolidatedStart = performance.now();

      // OPTIMIZATION #2: Single consolidated API call replaces 10 separate calls
      const [consolidated, socialData] = await Promise.all([
        reportsAPI.getConsolidatedDashboard(
          dealerId,
          startDate,
          endDate,
          30, // radius
          districtManager,
          regionalManager
        ),
        reportsAPI.getMlbSocialSummary(startDate, endDate).catch(() => null)
      ]);
      if (socialData) setMlbSocialData({ views: socialData.views || 0, engagement: socialData.engagement || 0 });

      const consolidatedEnd = performance.now();
      console.log(`[OPTIMIZATION #2] Consolidated endpoint took ${(consolidatedEnd - consolidatedStart).toFixed(2)}ms`);
      console.log('[CONSOLIDATED] Response structure:', Object.keys(consolidated));

      // Extract data from consolidated response
      const channels = consolidated.summary.channels;
      const charts = consolidated.charts;
      const mapResponse = consolidated.map;
      const spotsData = consolidated.spots;

      // Transform summary data
      const summary = {
        display: {
          impressions: channels.Display?.impressions || 0,
          clicks: channels.Display?.clicks || 0
        },
        dooh: {
          impressions: channels.DOOH?.impressions || 0,
          clicks: channels.DOOH?.clicks || 0
        },
        streaming_tv: {
          impressions: channels['Streaming TV']?.impressions || 0,
          clicks: channels['Streaming TV']?.clicks || 0
        },
        broadcast_tv: {
          impressions: spotsData.summary?.broadcast_tv_impressions || 0,
          clicks: channels['Broadcast TV']?.clicks || 0
        },
        youtube: {
          impressions: channels.YouTube?.impressions || 0,
          clicks: channels.YouTube?.clicks || 0
        },
        social: {
          impressions: channels.Social?.impressions || 0,
          clicks: channels.Social?.clicks || 0
        },
        paid_social: {
          impressions: channels['Paid Social']?.impressions || 0,
          clicks: channels['Paid Social']?.clicks || 0
        },
        streaming_audio: {
          impressions: channels['Streaming Audio']?.impressions || 0,
          clicks: 0
        },
        radio: {
          impressions: spotsData.summary?.radio_impressions || 0,
          clicks: 0
        },
        sport_signage: {
          impressions: spotsData.summary?.sport_signage_impressions || 0,
          clicks: 0
        },
        store_visits: channels['Store Visits']?.impressions || 0
      };

      setSpotsData(spotsData);
      console.log('[CORPORATE] Spots data:', spotsData);

      // Transform map data for MapView component
      const mapData: any = { display: [], dooh: [], streaming_tv: [] };

      if (mapResponse.zip_codes && Array.isArray(mapResponse.zip_codes)) {
        mapResponse.zip_codes.forEach((zipData: any) => {
          const coordinates: [number, number] | undefined =
            (zipData.longitude && zipData.latitude)
              ? [zipData.longitude, zipData.latitude]
              : undefined;

          if (zipData.channels && Array.isArray(zipData.channels)) {
            zipData.channels.forEach((channelData: any) => {
              const channelKey = channelData.channel.toLowerCase().replace(' ', '_');

              if (mapData[channelKey]) {
                mapData[channelKey].push({
                  zip: zipData.zip_code,
                  impressions: channelData.impressions || 0,
                  coordinates: coordinates,
                  channels: zipData.channels,
                  city: zipData.city,
                  state: zipData.state
                });
              }
            });
          }
        });
      }

      console.log('Transformed map data:', mapData);

      // Set dealer locations for map.
      // Non-corporate views return a single dealer_location object.
      // Corporate view returns an empty placeholder — dealer locations are
      // set later by fetchCorporateMapData when the user opens Map View.
      if (mapResponse.dealer_locations && Array.isArray(mapResponse.dealer_locations) && mapResponse.dealer_locations.length > 0) {
        setDealerLocations(mapResponse.dealer_locations.map((loc: any) => ({
          dealer_id: loc.dealer_id,
          name: loc.name,
          latitude: loc.latitude,
          longitude: loc.longitude,
          address: loc.address,
          city: loc.city,
          state: loc.state,
          zip: loc.zip
        })));
      } else if (mapResponse.dealer_location) {
        // Legacy single-dealer format (dealer/district/region views)
        const dealerLoc = mapResponse.dealer_location;
        setDealerLocations([{
          dealer_id: dealerLoc.dealer_id,
          name: dealerLoc.name,
          latitude: dealerLoc.latitude,
          longitude: dealerLoc.longitude,
          address: dealerLoc.address,
          city: dealerLoc.city,
          state: dealerLoc.state,
          zip: dealerLoc.zip
        }]);
      } else {
        setDealerLocations([]);
      }

      setReportData({
        summary,
        display_chart: charts.Display || [],
        dooh_chart: charts.DOOH || [],
        streaming_tv_chart: charts['Streaming TV'] || [],
        streaming_audio_chart: charts['Streaming Audio'] || [],
        broadcast_tv_chart: charts['Broadcast TV'] || [],
        radio_chart: charts.Radio || [],
        youtube_chart: charts.YouTube || [],
        search_chart: charts.Search || [],
        social_chart: charts.Social || [],
        paid_social_chart: charts['Paid Social'] || [],
        store_visits_chart: [],
        sport_signage_chart: charts['Sport Signage'] || [],
        sport_signage: spotsData.sport_signage || [],
        radio: spotsData.radio || [],
        television: spotsData.television || [],
        map: mapData
      });

      console.log('[OPTIMIZATION #2] Dashboard data loaded successfully from consolidated endpoint');
    } catch (err: any) {
      console.error('[CONSOLIDATED] Error - falling back would go here:', err);
      setError(err.response?.data?.error || 'Failed to load report data');
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  // Lazy-load corporate map data only when user opens the Map View tab.
  // The consolidated endpoint no longer includes map data for the corporate (all-dealers)
  // view — it was the single biggest bottleneck (~10s) on initial load.
  const fetchCorporateMapData = async () => {
    if (mapLoadedRef.current || mapLoading) return;
    setMapLoading(true);
    try {
      const startDate = format(dateRange.startDate, 'yyyy-MM-dd');
      const endDate = format(dateRange.endDate, 'yyyy-MM-dd');
      const mapResponse = await reportsAPI.getCorporateMapData(startDate, endDate);

      const mapData: any = { display: [], dooh: [], streaming_tv: [] };
      if (mapResponse.zip_codes && Array.isArray(mapResponse.zip_codes)) {
        mapResponse.zip_codes.forEach((zipData: any) => {
          const coordinates: [number, number] | undefined =
            (zipData.longitude && zipData.latitude)
              ? [zipData.longitude, zipData.latitude]
              : undefined;
          if (zipData.channels && Array.isArray(zipData.channels)) {
            zipData.channels.forEach((channelData: any) => {
              const channelKey = channelData.channel.toLowerCase().replace(' ', '_');
              if (mapData[channelKey]) {
                mapData[channelKey].push({
                  zip: zipData.zip_code,
                  impressions: channelData.impressions || 0,
                  coordinates,
                  channels: zipData.channels,
                  city: zipData.city,
                  state: zipData.state
                });
              }
            });
          }
        });
      }

      mapLoadedRef.current = true;
      setReportData(prev => prev ? { ...prev, map: mapData } : prev);

      // Populate dealer location pins from the map response
      if (mapResponse.dealer_locations && Array.isArray(mapResponse.dealer_locations)) {
        setDealerLocations(mapResponse.dealer_locations.map((loc: any) => ({
          dealer_id: loc.dealer_id,
          name: loc.name,
          latitude: loc.latitude,
          longitude: loc.longitude,
          address: loc.address,
          city: loc.city,
          state: loc.state,
          zip: loc.zip
        })));
      }
    } catch (err) {
      console.error('[CORPORATE MAP] Failed to load map data:', err);
    } finally {
      setMapLoading(false);
    }
  };

  const handleDateChange = (startDate: Date, endDate: Date) => {
    setDateRange({ startDate, endDate });
  };

  const handleDistributorChange = (distributor: Distributor | null) => {
    setSelectedDistributor(distributor);
  };

  const handleRegionChange = (region: Region | null) => {
    setSelectedRegion(region);
    setSelectedDistributor(null);
    if (!region) {
      // Reset to show all distributors
      fetchDistributors();
    }
  };

  const handleDistrictChange = (district: District | null) => {
    setSelectedDistrict(district);
    setSelectedDistributor(null);
  };

  const handleClearFilters = () => {
    setSelectedRegion(null);
    setSelectedDistrict(null);
    setSelectedDistributor(null);
    fetchDistributors(); // Reset to all distributors
  };

  const handleBackToCorporate = () => {
    setSelectedRegion(null);
    setSelectedDistrict(null);
    setSelectedDistributor(null);
    fetchDistributors();
  };

  // Determine which view to show based on selections
  const currentView = useMemo(() => {
    if (selectedDistributor) {
      return 'dealer';
    } else if (selectedDistrict) {
      return 'district';
    } else if (selectedRegion) {
      return 'region';
    }
    return 'corporate';
  }, [selectedRegion, selectedDistrict, selectedDistributor]);

  // Trigger lazy map fetch when user switches to map tab in corporate view.
  // Must be declared after currentView useMemo to satisfy TypeScript block-scope rules.
  useEffect(() => {
    if (viewMode === 'map' && currentView === 'corporate' && reportData && !mapLoadedRef.current) {
      fetchCorporateMapData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, currentView, reportData]);

  // Build breadcrumb items
  const breadcrumbItems = useMemo(() => {
    const items = [
      {
        label: 'Corporate',
        onClick: handleBackToCorporate,
        active: currentView === 'corporate'
      }
    ];

    if (selectedRegion) {
      items.push({
        label: selectedRegion.name,
        onClick: () => {
          setSelectedDistrict(null);
          setSelectedDistributor(null);
        },
        active: currentView === 'region'
      });
    }

    if (selectedDistrict) {
      items.push({
        label: selectedDistrict.name,
        onClick: () => {
          setSelectedDistributor(null);
        },
        active: currentView === 'district'
      });
    }

    if (selectedDistributor) {
      items.push({
        label: selectedDistributor.name,
        active: true
      } as any);
    }

    return items;
  }, [currentView, selectedRegion, selectedDistrict, selectedDistributor]);

  // Format number with commas
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };

  // Format currency
  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(num);
  };

  // Helper to get impressions from channel metrics (handles both number and object)
  const getImpressions = (value: number | { impressions: number; clicks: number }): number => {
    if (typeof value === 'object' && value !== null) {
      return value.impressions;
    }
    return value || 0;
  };

  if (isLoading && !reportData) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading data...</p>
      </div>
    );
  }

  if (error && distributors.length === 0) {
    return (
      <div className="dashboard-error">
        <p>Error: {error}</p>
        <button onClick={fetchDistributors}>Retry</button>
      </div>
    );
  }

  // If a dealer is selected, show the dealer dashboard
  console.log('[CORPORATE DASHBOARD] Checking dealer view condition:', {
    selectedDistributor: selectedDistributor,
    selectedDistributor_id: selectedDistributor?.distributor_id,
    selectedDistributor_name: selectedDistributor?.name,
    currentView: currentView,
    willRenderDealerDashboard: !!(selectedDistributor && currentView === 'dealer')
  });

  if (selectedDistributor && currentView === 'dealer') {
    console.log('[CORPORATE DASHBOARD] ⚠️ RENDERING DEALER DASHBOARD FROM CORPORATE!');
    return (
      <DealerDashboard
        selectedDistributor={selectedDistributor}
        onBackToCorporate={handleBackToCorporate}
        isCorporateView={true}
      />
    );
  }

  // Build subtitle with region/district context
  const getSubtitle = () => {
    if (currentView === 'region' && selectedRegion) {
      return `Regional Manager: ${selectedRegion.manager}`;
    } else if (currentView === 'district' && selectedDistrict) {
      return `District Manager: ${selectedDistrict.manager}`;
    }
    return user?.username || 'Corporate User';
  };

  // Build title based on view
  const getTitle = () => {
    if (currentView === 'region') return `Regional View - ${selectedRegion?.name}`;
    if (currentView === 'district') return `District View - ${selectedDistrict?.name}`;
    return 'Corporate Dashboard';
  };

  // Define tabs for corporate dashboard
  const tabs: Tab[] = [
    {
      id: 'overview',
      label: 'Overview',
      icon: 'dashboard',
      content: renderOverviewTab()
    },
    {
      id: 'user-management',
      label: 'User Management',
      icon: 'people',
      content: (
        <div className="tab-content-wrapper">
          <div className="tab-scope-message">
            <i className="material-icons">info</i>
            <span>Showing all users across all regions (corporate view - no filtering applied)</span>
          </div>
          <UserManagementView
            currentUserRole={user?.role}
            scopeMessage="Corporate users see all users system-wide. Hierarchical filtering is automatically handled by the backend based on your role."
          />
        </div>
      )
    },
    {
      id: 'coverage',
      label: 'Coverage Analysis',
      icon: 'analytics',
      content: (
        <div className="tab-content-wrapper">
          <div className="tab-scope-message">
            <i className="material-icons">info</i>
            <span>Showing coverage analysis for all dealers across all regions</span>
          </div>
          <CoverageAnalysisView
            hierarchicalScope="corporate"
            scopeMessage="Corporate view shows all dealers system-wide with no regional filtering."
          />
        </div>
      )
    },
    {
      id: 'alerts',
      label: 'Alert Messages',
      icon: 'campaign',
      content: (
        <div className="tab-content-wrapper">
          <AlertManagementView />
        </div>
      )
    }
  ];

  // Main dashboard render
  return (
    <div className="dashboard-container corporate-dashboard">
      <KnowledgePanel
        isOpen={knowledgePanelOpen}
        onClose={() => setKnowledgePanelOpen(false)}
        topic={currentTopic}
        title={getDefinition(currentTopic)?.title || ''}
        description={getDefinition(currentTopic)?.description || ''}
      />
      <DashboardHeader
        title={getTitle()}
        subtitle={getSubtitle()}
        breadcrumbs={breadcrumbItems.map(item => ({
          label: item.label,
          onClick: item.onClick
        }))}
        showBackButton={currentView !== 'corporate'}
        onBack={currentView !== 'corporate' ? handleBackToCorporate : undefined}
      />

      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </div>
  );

  // Helper function to render the Overview tab content
  function renderOverviewTab() {
    return (
      <>
        <div className="dashboard-controls">
          <div className="controls-wrapper">
            <div className="filter-controls">
              <RegionSelector
                regions={regions}
                selectedRegion={selectedRegion}
                onChange={handleRegionChange}
                placeholder="All Regions"
              />
              <DistrictSelector
                districts={districts}
                selectedDistrict={selectedDistrict}
                onChange={handleDistrictChange}
                placeholder={selectedRegion ? "All Districts" : "Select a region first"}
                disabled={!selectedRegion}
              />
              {currentView === 'district' && (
                <DistributorSelector
                  distributors={distributors}
                  selectedDistributor={selectedDistributor}
                  onChange={handleDistributorChange}
                  placeholder="All Dealers"
                />
              )}
              <DateSelector
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onDateChange={handleDateChange}
              />

              <div className="view-toggle">
                <button
                  className={viewMode === 'combined' ? 'active' : ''}
                  onClick={() => setViewMode('combined')}
                >
                  <i className="material-icons">dashboard</i>
                  Combined View
                </button>
                <button
                  className={viewMode === 'chart' ? 'active' : ''}
                  onClick={() => setViewMode('chart')}
                >
                  <i className="material-icons">show_chart</i>
                  Chart View
                </button>
                <button
                  className={viewMode === 'table' ? 'active' : ''}
                  onClick={() => setViewMode('table')}
                >
                  <i className="material-icons">table_chart</i>
                  Table View
                </button>
                <button
                  className={viewMode === 'map' ? 'active' : ''}
                  onClick={() => setViewMode('map')}
                >
                  <i className="material-icons">map</i>
                  Map View
                </button>
              </div>
            </div>

            <div className="controls-actions">
              {(selectedRegion || selectedDistrict || selectedDistributor) && (
                <button className="clear-filters-btn enhanced" onClick={handleClearFilters}>
                  <i className="material-icons">filter_alt_off</i>
                  <span>Clear Filters</span>
                </button>
              )}
              {currentView !== 'corporate' && (
                <button className="back-to-corporate-btn" onClick={handleBackToCorporate}>
                  <i className="material-icons">home</i>
                  <span>Corporate View</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <main className="dashboard-content">
        <div className="container">
          {error ? (
            <div className="content-error">
              <p>Error: {error}</p>
              <button onClick={fetchReportData}>Retry</button>
            </div>
          ) : reportData ? (
            <>
              {/* Metrics Section - Always visible except in map-only view */}
              {viewMode !== 'map' && (
                <section className="metrics-section">
                  <div className="metrics-grid">
                    {hasMetricData(reportData.summary.display) && (
                      <MetricCard
                        title="Display Impressions"
                        value={reportData.summary.display}
                        color={CHANNEL_COLORS.display}
                        onHelpClick={() => handleHelpClick('display')}
                      />
                    )}
                    {hasMetricData(reportData.summary.dooh) && (
                      <MetricCard
                        title="DOOH Impressions"
                        value={reportData.summary.dooh}
                        color={CHANNEL_COLORS.dooh}
                        hideClicks={true}
                        onHelpClick={() => handleHelpClick('dooh')}
                      />
                    )}
                    {hasMetricData(reportData.summary.streaming_tv) && (
                      <MetricCard
                        title="Streaming TV Impressions"
                        value={reportData.summary.streaming_tv}
                        color={CHANNEL_COLORS.streaming_tv}
                        hideClicks={true}
                        onHelpClick={() => handleHelpClick('streaming_tv')}
                      />
                    )}
                    {hasMetricData(spotsData?.summary?.broadcast_tv_impressions || 0) && (
                      <MetricCard
                        title="Broadcast TV"
                        value={spotsData?.summary?.broadcast_tv_impressions || 0}
                        color={CHANNEL_COLORS.broadcast_tv}
                        hideClicks={true}
                        onHelpClick={() => handleHelpClick('broadcast_tv')}
                      />
                    )}
                    {hasMetricData(reportData.summary.streaming_audio) && (
                      <MetricCard
                        title="Streaming Audio"
                        value={reportData.summary.streaming_audio}
                        color={CHANNEL_COLORS.streaming_audio}
                        hideClicks={true}
                        onHelpClick={() => handleHelpClick('streaming_audio')}
                      />
                    )}
                    {hasMetricData(spotsData?.summary?.radio_impressions || 0) && (
                      <MetricCard
                        title="Radio"
                        value={spotsData?.summary?.radio_impressions || 0}
                        color={CHANNEL_COLORS.radio}
                        hideClicks={true}
                        onHelpClick={() => handleHelpClick('radio')}
                      />
                    )}
                    {hasMetricData(spotsData?.summary?.sport_signage_impressions || 0) && (
                      <MetricCard
                        title="Sport Signage – MLB & NCAA"
                        value={spotsData?.summary?.sport_signage_impressions || 0}
                        color={CHANNEL_COLORS.sport_signage}
                        hideClicks={true}
                        onHelpClick={() => handleHelpClick('sport_signage')}
                      />
                    )}
                    {hasMetricData(reportData.summary.youtube) && (
                      <MetricCard
                        title="YouTube Impressions"
                        value={reportData.summary.youtube}
                        color={CHANNEL_COLORS.youtube}
                        onHelpClick={() => handleHelpClick('youtube')}
                      />
                    )}
                    {hasMetricData(reportData.summary.paid_social) && (
                      <MetricCard
                        title="Paid Social"
                        value={reportData.summary.paid_social}
                        color={CHANNEL_COLORS.paid_social}
                        onHelpClick={() => handleHelpClick('paid_social')}
                      />
                    )}
                    {hasMetricData(reportData.summary.store_visits) && (
                      <MetricCard
                        title="Store Visits"
                        value={reportData.summary.store_visits}
                        color={CHANNEL_COLORS.store_visits}
                        onHelpClick={() => handleHelpClick('store_visits')}
                      />
                    )}
                    {mlbSocialData && mlbSocialData.views > 0 && (
                      <MetricCard title="MLB National Social – Views" value={mlbSocialData.views} color="#1877F2" hideClicks={true} onHelpClick={() => handleHelpClick('mlb_social_views')} />
                    )}
                    {mlbSocialData && mlbSocialData.engagement > 0 && (
                      <MetricCard title="MLB National Social – Engagement" value={mlbSocialData.engagement} color="#1DA1F2" hideClicks={true} onHelpClick={() => handleHelpClick('mlb_social_engagement')} />
                    )}
                  </div>
                </section>
              )}

              {/* Combined View - Shows everything */}
              {viewMode === 'combined' && (
                <>
                  <LazyLoad fallback={<div className="chart-placeholder">Loading charts...</div>}>
                    <section className="charts-section">
                      <div className="charts-grid">
                        {hasChartData(reportData.display_chart) && (
                          <ComboChart
                            data={reportData.display_chart}
                            title="Display Performance"
                            color={CHANNEL_COLORS.display}
                          />
                        )}
                        {hasChartData(reportData.dooh_chart) && (
                          <ComboChart
                            data={reportData.dooh_chart}
                            title="DOOH Performance"
                            color={CHANNEL_COLORS.dooh}
                            showClicks={false}
                          />
                        )}
                        {hasChartData(reportData.streaming_tv_chart) && (
                          <ComboChart
                            data={reportData.streaming_tv_chart}
                            title="Streaming TV Performance"
                            color={CHANNEL_COLORS.streaming_tv}
                            showClicks={false}
                          />
                        )}
                        {hasChartData(reportData.youtube_chart) && (
                          <ComboChart
                            data={reportData.youtube_chart}
                            title="YouTube Performance"
                            color={CHANNEL_COLORS.youtube}
                          />
                        )}
                        {hasChartData(reportData.broadcast_tv_chart) && (
                          <ComboChart
                            data={reportData.broadcast_tv_chart}
                            title="Broadcast TV Performance"
                            color={CHANNEL_COLORS.broadcast_tv}
                            showClicks={false}
                          />
                        )}
                        {hasChartData(reportData.sport_signage_chart) && (
                          <ComboChart
                            data={reportData.sport_signage_chart}
                            title="Sport Signage Performance"
                            color={CHANNEL_COLORS.sport_signage}
                            showClicks={false}
                          />
                        )}
                        {hasChartData(reportData.streaming_audio_chart) && (
                          <ComboChart
                            data={reportData.streaming_audio_chart}
                            title="Streaming Audio Performance"
                            color={CHANNEL_COLORS.streaming_audio}
                            showClicks={false}
                          />
                        )}
                        {hasChartData(reportData.radio_chart) && (
                          <ComboChart
                            data={reportData.radio_chart}
                            title="Radio Performance"
                            color={CHANNEL_COLORS.radio}
                            showClicks={false}
                          />
                        )}
                        {hasChartData(reportData.paid_social_chart) && (
                          <ComboChart
                            data={reportData.paid_social_chart}
                            title="Paid Social Performance"
                            color={CHANNEL_COLORS.paid_social}
                          />
                        )}
                        {hasChartData(reportData.store_visits_chart) && (
                          <ComboChart
                            data={reportData.store_visits_chart}
                            title="Store Visits Performance"
                            color={CHANNEL_COLORS.store_visits}
                            showClicks={false}
                          />
                        )}
                      </div>
                    </section>
                  </LazyLoad>

                  <LazyLoad fallback={<div className="lazy-placeholder">Loading tables...</div>}>
                    <section className="table-section">
                      {renderTableContent()}
                    </section>

                    {hasTableData(reportData.radio) && (
                      <section className="radio-section">
                        <RadioTable data={reportData.radio} onHelpClick={() => handleHelpClick('radio')} />
                      </section>
                    )}

                    {hasTableData(reportData.television) && (
                      <section className="television-section">
                        <TelevisionTable data={reportData.television} onHelpClick={() => handleHelpClick('broadcast_tv')} />
                      </section>
                    )}

                    {hasTableData(reportData.sport_signage) && (
                      <section className="sport-signage-section">
                        <SportSignageTable data={reportData.sport_signage} onHelpClick={() => handleHelpClick('sport_signage')} />
                      </section>
                    )}
                  </LazyLoad>
                </>
              )}

              {/* Chart View */}
              {viewMode === 'chart' && (
                <>
                  <LazyLoad fallback={<div className="chart-placeholder">Loading charts...</div>}>
                    <section className="charts-section">
                      <div className="charts-grid">
                        {hasChartData(reportData.display_chart) && (
                          <ComboChart
                            data={reportData.display_chart}
                            title="Display Performance"
                            color={CHANNEL_COLORS.display}
                          />
                        )}
                        {hasChartData(reportData.dooh_chart) && (
                          <ComboChart
                            data={reportData.dooh_chart}
                            title="DOOH Performance"
                            color={CHANNEL_COLORS.dooh}
                            showClicks={false}
                          />
                        )}
                        {hasChartData(reportData.streaming_tv_chart) && (
                          <ComboChart
                            data={reportData.streaming_tv_chart}
                            title="Streaming TV Performance"
                            color={CHANNEL_COLORS.streaming_tv}
                            showClicks={false}
                          />
                        )}
                        {hasChartData(reportData.youtube_chart) && (
                          <ComboChart
                            data={reportData.youtube_chart}
                            title="YouTube Performance"
                            color={CHANNEL_COLORS.youtube}
                          />
                        )}
                        {hasChartData(reportData.broadcast_tv_chart) && (
                          <ComboChart
                            data={reportData.broadcast_tv_chart}
                            title="Broadcast TV Performance"
                            color={CHANNEL_COLORS.broadcast_tv}
                            showClicks={false}
                          />
                        )}
                        {hasChartData(reportData.sport_signage_chart) && (
                          <ComboChart
                            data={reportData.sport_signage_chart}
                            title="Sport Signage Performance"
                            color={CHANNEL_COLORS.sport_signage}
                            showClicks={false}
                          />
                        )}
                        {hasChartData(reportData.streaming_audio_chart) && (
                          <ComboChart
                            data={reportData.streaming_audio_chart}
                            title="Streaming Audio Performance"
                            color={CHANNEL_COLORS.streaming_audio}
                            showClicks={false}
                          />
                        )}
                        {hasChartData(reportData.radio_chart) && (
                          <ComboChart
                            data={reportData.radio_chart}
                            title="Radio Performance"
                            color={CHANNEL_COLORS.radio}
                            showClicks={false}
                          />
                        )}
                        {hasChartData(reportData.paid_social_chart) && (
                          <ComboChart
                            data={reportData.paid_social_chart}
                            title="Paid Social Performance"
                            color={CHANNEL_COLORS.paid_social}
                          />
                        )}
                        {hasChartData(reportData.store_visits_chart) && (
                          <ComboChart
                            data={reportData.store_visits_chart}
                            title="Store Visits Performance"
                            color={CHANNEL_COLORS.store_visits}
                            showClicks={false}
                          />
                        )}
                      </div>
                    </section>
                  </LazyLoad>
                  <LazyLoad fallback={<div className="lazy-placeholder">Loading tables...</div>}>
                    {hasTableData(reportData.radio) && (
                      <section className="radio-section">
                        <RadioTable data={reportData.radio} onHelpClick={() => handleHelpClick('radio')} />
                      </section>
                    )}

                    {hasTableData(reportData.television) && (
                      <section className="television-section">
                        <TelevisionTable data={reportData.television} onHelpClick={() => handleHelpClick('broadcast_tv')} />
                      </section>
                    )}

                    {hasTableData(reportData.sport_signage) && (
                      <section className="sport-signage-section">
                        <SportSignageTable data={reportData.sport_signage} onHelpClick={() => handleHelpClick('sport_signage')} />
                      </section>
                    )}
                  </LazyLoad>
                </>
              )}

              {/* Table View */}
              {viewMode === 'table' && (
                <LazyLoad fallback={<div className="lazy-placeholder">Loading tables...</div>}>
                  <section className="table-section">
                    {renderTableContent()}
                  </section>
                  {hasTableData(reportData.radio) && (
                    <section className="radio-section">
                      <RadioTable data={reportData.radio} />
                    </section>
                  )}

                  {hasTableData(reportData.television) && (
                    <section className="television-section">
                      <TelevisionTable data={reportData.television} />
                    </section>
                  )}
                </LazyLoad>
              )}

              {/* Map View */}
              {viewMode === 'map' && (
                <section className="map-section">
                  {mapLoading && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      <div className="skeleton-spin" style={{ width: 32, height: 32, margin: '0 auto 12px', border: '3px solid #eee', borderTopColor: '#DA291C', borderRadius: '50%' }} />
                      <p>Loading map data...</p>
                    </div>
                  )}
                  {!mapLoading && (
                    <MapView
                      data={reportData.map}
                      viewLevel={currentView as 'dealer' | 'district' | 'region' | 'corporate'}
                      dealerLocations={dealerLocations}
                    />
                  )}
                </section>
              )}
            </>
          ) : (
            <div className="no-data">No data available</div>
          )}
        </div>
        </main>
      </>
    );
  }

  // Helper function to render table content based on current view
  function renderTableContent() {
    switch (currentView) {
      case 'corporate':
        return (
          <>
            <h2>Regional Performance</h2>
            <div className="data-table-container">
              <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Manager</th>
                    <th>Districts</th>
                    <th>Dealers</th>
                    <th>Display</th>
                    <th>DOOH</th>
                    <th>Streaming TV</th>
                    <th>Streaming Audio</th>
                    <th>Broadcast TV</th>
                    <th>Radio</th>
                    <th>Sport Signage</th>
                    <th>Store Visits</th>
                  </tr>
                </thead>
                <tbody>
                  {regions.map(region => {
                    const ch = region.channels || {};
                    const fmt = (v: number | undefined) => v && v > 0 ? formatNumber(v) : <span style={{ color: '#999' }}>—</span>;
                    return (
                    <tr key={region.id}>
                      <td>
                        <div className="label-with-action">
                          <span>{region.manager}</span>
                          <button className="table-action-btn primary" onClick={() => handleRegionChange(region)}>View Region</button>
                        </div>
                      </td>
                      <td>{region.district_count}</td>
                      <td>{region.dealer_count}</td>
                      <td>{fmt(ch['Display'])}</td>
                      <td>{fmt(ch['DOOH'])}</td>
                      <td>{fmt(ch['Streaming TV'])}</td>
                      <td>{fmt(ch['Streaming Audio'])}</td>
                      <td>{fmt(ch['Broadcast TV'])}</td>
                      <td>{fmt(ch['Radio'])}</td>
                      <td>{fmt(ch['Sport Signage'])}</td>
                      <td>{fmt(ch['Store Visits'])}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          </>
        );

      case 'region':
        return (
          <>
            <h2>District Performance - {selectedRegion?.name}</h2>
            <div className="data-table-container">
              <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>District</th>
                    <th>Manager</th>
                    <th>Dealers</th>
                    <th>Display</th>
                    <th>DOOH</th>
                    <th>Streaming TV</th>
                    <th>Streaming Audio</th>
                    <th>Broadcast TV</th>
                    <th>Radio</th>
                    <th>Sport Signage</th>
                    <th>Store Visits</th>
                  </tr>
                </thead>
                <tbody>
                  {districts.map(district => {
                    const ch = district.channels || {};
                    const fmt = (v: number | undefined) => v && v > 0 ? formatNumber(v) : <span style={{ color: '#999' }}>—</span>;
                    return (
                    <tr key={district.id}>
                      <td>
                        <div className="label-with-action">
                          <span>{district.name}</span>
                          <button className="table-action-btn primary" onClick={() => navigate('/district', { state: { districtId: district.id, districtName: district.name, districtManager: district.username } })}>View District</button>
                        </div>
                      </td>
                      <td>{district.manager}</td>
                      <td>{district.dealer_count}</td>
                      <td>{fmt(ch['Display'])}</td>
                      <td>{fmt(ch['DOOH'])}</td>
                      <td>{fmt(ch['Streaming TV'])}</td>
                      <td>{fmt(ch['Streaming Audio'])}</td>
                      <td>{fmt(ch['Broadcast TV'])}</td>
                      <td>{fmt(ch['Radio'])}</td>
                      <td>{fmt(ch['Sport Signage'])}</td>
                      <td>{fmt(ch['Store Visits'])}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          </>
        );

      case 'district':
        return (
          <>
            <h2>Dealer Performance - {selectedDistrict?.name}</h2>
            <div className="data-table-container">
              <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Dealer</th>
                    <th>DMA</th>
                    <th>Total Impressions</th>
                  </tr>
                </thead>
                <tbody>
                  {distributors.map((dealer: Distributor) => {
                    const isSelected = selectedDistributor?.distributor_id === dealer.distributor_id;
                    const impressions = (dealer as any).total_impressions || 0;
                    return (
                      <tr key={dealer.distributor_id} className={isSelected ? 'selected' : ''}>
                        <td>
                          <div className="label-with-action">
                            <span>{dealer.name}</span>
                            <button className="table-action-btn primary" onClick={() => navigate('/dashboard', { state: { selectedDistributor: dealer } })}>View Dealer</button>
                          </div>
                        </td>
                        <td>{dealer.city || 'N/A'}</td>
                        <td>{formatNumber(impressions)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  }
};

export default CorporateDashboard;
